// A photo's size from the first bytes of its file (JPEG or PNG), turned the
// way it shows (EXIF orientation), without decoding the picture: a 12 MP
// still decoded just to measure it costs tens of MB and a few hundred ms on
// a low-end phone. Pure on a DataView; unit-tested.

export interface Size {
  w: number
  h: number
}

// The header is near the start; past this, give up (the caller decodes).
const HEAD_BYTES = 256 * 1024

export async function imageSize(photo: Blob): Promise<Size | null> {
  const head = new DataView(await photo.slice(0, HEAD_BYTES).arrayBuffer())
  return pngSize(head) ?? jpegSize(head)
}

export function pngSize(v: DataView): Size | null {
  // The signature, then the IHDR chunk with width and height.
  if (v.byteLength < 24 || v.getUint32(0) !== 0x89504e47 || v.getUint32(12) !== 0x49484452) return null
  return { w: v.getUint32(16), h: v.getUint32(20) }
}

export function jpegSize(v: DataView): Size | null {
  if (v.byteLength < 4 || v.getUint16(0) !== 0xffd8) return null
  let orientation = 1
  let at = 2
  while (at + 4 <= v.byteLength) {
    if (v.getUint8(at) !== 0xff) return null
    const marker = v.getUint8(at + 1)
    // Fill bytes, and markers with no length.
    if (marker === 0xff) {
      at++
      continue
    }
    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd8)) {
      at += 2
      continue
    }
    const length = v.getUint16(at + 2)
    if (marker === 0xe1) orientation = exifOrientation(v, at + 4, length - 2) ?? orientation
    // A frame header (SOF0–SOF15, not DHT / JPG / DAC): the size.
    if (marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc) {
      if (at + 9 > v.byteLength) return null
      const h = v.getUint16(at + 5)
      const w = v.getUint16(at + 7)
      // Orientations 5–8 turn the picture a quarter.
      return orientation >= 5 && orientation <= 8 ? { w: h, h: w } : { w, h }
    }
    // The picture data starts before any frame header: not one we can read.
    if (marker === 0xda) return null
    at += 2 + length
  }
  return null
}

function exifOrientation(v: DataView, start: number, length: number): number | null {
  // "Exif\0\0", then a TIFF header (II = little-endian, MM = big).
  if (length < 14 || start + 14 > v.byteLength) return null
  if (v.getUint32(start) !== 0x45786966 || v.getUint16(start + 4) !== 0) return null
  const tiff = start + 6
  const little = v.getUint16(tiff) === 0x4949
  if (v.getUint16(tiff + 2, little) !== 42) return null
  const ifd = tiff + v.getUint32(tiff + 4, little)
  if (ifd + 2 > v.byteLength) return null
  const count = v.getUint16(ifd, little)
  for (let i = 0; i < count; i++) {
    const entry = ifd + 2 + i * 12
    if (entry + 12 > v.byteLength) return null
    if (v.getUint16(entry, little) === 0x0112) return v.getUint16(entry + 8, little)
  }
  return null
}
